import React, { useEffect } from "react";
import { Routes, Route, Navigate, Outlet } from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";
import { useDeviceToken } from "./hooks/useDeviceToken";
import { ensureDeviceToken } from "./utils/deviceToken";

// Auth Components
import Login from "./components/auth/Login";
import ProtectedRoute from "./components/auth/ProtectedRoute";

// Dashboard
import Dashboard from "./components/dashboard/Dashboard";

// Vehicles
import VehicleList from "./components/vehicles/VehicleList";

// Inspections
import InspectionList from "./components/inspections/InspectionList";
import CreateInspection from "./components/inspections/CreateInspection";
import EditInspection from "./components/inspections/EditInspection";
import InspectionDetails from "./components/inspections/InspectionDetails";
import InspectionBoard from "./components/InspectionBoard";

// Admin
import IPManagement from "./components/admin/IPManagement";
import IPCheckGuard from "./components/admin/IPCheckGuard";
import UserManagement from "./components/admin/UserManagement";
import WorkshopManagement from "./components/admin/WorkshopManagement";
import DeviceTokenManagement from "./components/admin/DeviceTokenManagement";

// Settings
import ProfileSettings from "./components/settings/ProfileSettings";

// Notifications
import NotificationsPage from "./components/notifications/NotificationsPage";

// Common
import Layout from "./components/common/Layout";
import LoadingSpinner from "./components/common/LoadingSpinner";
import WorkshopWrapper from "./components/WorkshopWrapper";

// Permissions
import { Permission } from "./types/roles";

const App: React.FC = () => {
  const { user, loading, isAuthenticated } = useAuth();

  // Ensure device token exists on app load
  useEffect(() => {
    ensureDeviceToken();
  }, []);

  // Monitor device token to ensure it's always present
  useDeviceToken();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return (
      <IPCheckGuard>
        <Login />
      </IPCheckGuard>
    );
  }

  return (
    <IPCheckGuard>
      <WorkshopWrapper>
        <Routes>
          {/* Inspection Board - Full Screen (No Layout) - Accessible to all authenticated users */}
          <Route
            path="/inspection-board"
            element={
              <ProtectedRoute
                requiredPermission={Permission.VIEW_INSPECTION_BOARD}
              >
                <InspectionBoard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inspections-board"
            element={
              <ProtectedRoute
                requiredPermission={Permission.VIEW_INSPECTION_BOARD}
              >
                <InspectionBoard />
              </ProtectedRoute>
            }
          />

          {/* Protected Routes with Layout */}
          <Route
            element={
              <Layout>
                <Outlet />
              </Layout>
            }
          >
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            {/* Dashboard - All authenticated users */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute requiredPermission={Permission.VIEW_DASHBOARD}>
                  <Dashboard />
                </ProtectedRoute>
              }
            />

            {/* Vehicles - All authenticated users can view */}
            <Route
              path="/vehicles"
              element={
                <ProtectedRoute requiredPermission={Permission.VIEW_VEHICLES}>
                  <VehicleList />
                </ProtectedRoute>
              }
            />

            {/* Inspections - All authenticated users can view, create and edit */}
            <Route
              path="/inspections"
              element={
                <ProtectedRoute
                  requiredPermission={Permission.VIEW_INSPECTIONS}
                >
                  <InspectionList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/inspections/new"
              element={
                <ProtectedRoute
                  requiredPermission={Permission.CREATE_INSPECTION}
                >
                  <CreateInspection />
                </ProtectedRoute>
              }
            />
            <Route
              path="/inspections/:id/edit"
              element={
                <ProtectedRoute requiredPermission={Permission.EDIT_INSPECTION}>
                  <EditInspection />
                </ProtectedRoute>
              }
            />
            <Route
              path="/inspections/:id/details"
              element={
                <ProtectedRoute
                  requiredPermission={Permission.VIEW_INSPECTIONS}
                >
                  <InspectionDetails />
                </ProtectedRoute>
              }
            />

            {/* Notifications - All authenticated users */}
            <Route
              path="/notifications"
              element={
                <ProtectedRoute
                  requiredPermission={Permission.VIEW_NOTIFICATIONS}
                >
                  <NotificationsPage />
                </ProtectedRoute>
              }
            />

            {/* Settings - All authenticated users */}
            <Route
              path="/settings/profile"
              element={
                <ProtectedRoute>
                  <ProfileSettings />
                </ProtectedRoute>
              }
            />

            {/* Admin Only Routes */}
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute adminOnly={true}>
                  <UserManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/workshops"
              element={
                <ProtectedRoute adminOnly={true}>
                  <WorkshopManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/ip-access"
              element={
                <ProtectedRoute adminOnly={true}>
                  <IPManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/device-tokens"
              element={
                <ProtectedRoute adminOnly={true}>
                  <DeviceTokenManagement />
                </ProtectedRoute>
              }
            />
            {/* Legacy route for IP Management */}
            <Route
              path="/settings/ip-access"
              element={<Navigate to="/admin/ip-access" replace />}
            />

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Route>
        </Routes>
      </WorkshopWrapper>
    </IPCheckGuard>
  );
};

export default App;
